import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { cva } from "class-variance-authority";
import { Alert, AlertDescription } from "../ui/alert";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { cn } from "../../lib/utils";
import {
  applyHunkVariationText,
  countAppliedHunks,
  countChangedHunks,
  diffChapterParagraphs,
  diffWordSpans,
  findMatchingOriginalHunk,
  hunkHasMaterialChange,
  hunkSummary,
  hunkVariationDiffersFromOriginal,
  hunkVariationText,
  mergeSelectedHunks,
  preserveHunkPreviewState,
  rebuildVariationContentFromHunks,
  splitIntoParagraphs,
  type DiffSpan,
  type ParagraphDiffHunk,
} from "../../../shared/chapterParagraphDiff";
import type { ChapterVariation } from "../../../shared/models";
import { variationContentToPlainText } from "../../../shared/chapterVariationRichText";

const comparisonGridClassName =
  "grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(8rem,12rem)_7.5rem]";

const diffSpanVariants = cva("", {
  variants: {
    tone: {
      equal: "",
      remove: "bg-destructive/30 line-through decoration-destructive/70",
      add: "bg-emerald-500/30",
    },
  },
});

const blockTextVariants = cva(
  "m-0 text-[0.92rem] leading-[1.65] whitespace-pre-wrap",
  {
    variants: {
      tone: {
        neutral: "",
        remove: "bg-destructive/10 line-through",
        add: "bg-emerald-500/10",
      },
    },
    defaultVariants: {
      tone: "neutral",
    },
  }
);

const comparisonCellVariants = cva(
  "min-w-0 border-l border-border/85 px-3 py-2.5 first:border-l-0",
  {
    variants: {
      state: {
        neutral: "",
        equal: "bg-muted/10",
        removed: "bg-destructive/15",
        added: "bg-emerald-500/15",
        applied: "bg-emerald-500/5",
        empty: "bg-muted/20",
        editing: "bg-emerald-500/10",
      },
    },
    defaultVariants: {
      state: "neutral",
    },
  }
);

const hunkBadgeVariants = cva(
  "mb-1.5 h-auto rounded-md px-1.5 py-0.5 text-[0.65rem] font-semibold tracking-[0.04em] uppercase",
  {
    variants: {
      kind: {
        equal: "bg-muted text-muted-foreground",
        removed: "bg-destructive/10 text-destructive",
        added: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
        changed: "bg-amber-500/10 text-amber-800 dark:text-amber-300",
      },
    },
  }
);

type Props = {
  chapterTitle: string;
  variation: ChapterVariation;
  /** Live chapter text (TipTap JSON or plain) — preferred over stored baseContent. */
  liveBaseContent?: string;
  onApply: (mergedContent: string, hunks: ParagraphDiffHunk[]) => void;
  onClose: () => void;
  onVariationContentChange?: (content: string) => void;
};

function effectiveBaseContent(variation: ChapterVariation, liveBaseContent?: string): string {
  const stored = variationContentToPlainText(variation.baseContent).trim();
  const live = variationContentToPlainText(liveBaseContent ?? "").trim();
  if (!live) return stored;

  const storedParagraphs = splitIntoParagraphs(stored).length;
  const variationParagraphs = splitIntoParagraphs(
    variationContentToPlainText(variation.content)
  ).length;
  const liveParagraphs = splitIntoParagraphs(live).length;

  if (storedParagraphs <= 1 && (variationParagraphs > 1 || liveParagraphs > 1)) {
    return live;
  }
  return stored || live;
}

function renderDiffSpans(spans: DiffSpan[], keyPrefix: string): ReactNode {
  if (!spans.length) {
    return <div className="text-sm italic text-muted-foreground" aria-hidden>—</div>;
  }
  return (
    <p className="m-0 text-[0.92rem] leading-[1.65] whitespace-pre-wrap">
      {spans.map((span, index) => {
        return (
          <span
            key={`${keyPrefix}-${index}`}
            className={diffSpanVariants({ tone: span.type })}
          >
            {index > 0 ? " " : ""}
            {span.text}
          </span>
        );
      })}
    </p>
  );
}

function renderBlockText(paragraphs: string[], keyPrefix: string, tone: "remove" | "add" | "neutral"): ReactNode {
  if (!paragraphs.length) {
    return <div className="text-sm italic text-muted-foreground" aria-hidden>—</div>;
  }
  const text = paragraphs.join(" ");
  return (
    <p key={keyPrefix} className={blockTextVariants({ tone })}>
      {text}
    </p>
  );
}

function renderHunkSide(
  hunk: ParagraphDiffHunk,
  side: "base" | "variation",
  applied: boolean
): ReactNode {
  const baseText = hunk.baseParagraphs.join(" ");
  const variationText = hunk.variationParagraphs.join(" ");

  if (hunk.kind === "equal") {
    return renderBlockText(hunk.baseParagraphs, `${hunk.id}-equal`, "neutral");
  }

  if (hunk.kind === "removed") {
    if (side === "variation") return <div className="text-sm italic text-muted-foreground">Deleted</div>;
    if (applied) return <div className="text-sm italic text-muted-foreground">Removed from preview</div>;
    return renderBlockText(hunk.baseParagraphs, `${hunk.id}-removed`, "remove");
  }

  if (hunk.kind === "added") {
    if (side === "base") {
      return applied ? renderBlockText(hunk.variationParagraphs, `${hunk.id}-added-preview`, "neutral") : (
        <div className="text-sm italic text-muted-foreground">Not in manuscript</div>
      );
    }
    return renderBlockText(hunk.variationParagraphs, `${hunk.id}-added`, "add");
  }

  if (side === "base") {
    if (applied) {
      return renderBlockText(hunk.variationParagraphs, `${hunk.id}-applied`, "neutral");
    }
    const { base } = diffWordSpans(baseText, variationText);
    return renderDiffSpans(base, `${hunk.id}-base-words`);
  }

  const { variation } = diffWordSpans(baseText, variationText);
  return renderDiffSpans(variation, `${hunk.id}-variation-words`);
}

function hunkIsEditable(hunk: ParagraphDiffHunk): boolean {
  return hunkHasMaterialChange(hunk) && hunk.kind !== "removed";
}

export function ChapterCompareOverlay({
  chapterTitle,
  variation,
  liveBaseContent,
  onApply,
  onClose,
  onVariationContentChange,
}: Props) {
  const canEdit = variation.status === "open" && Boolean(onVariationContentChange);

  const baseText = useMemo(
    () => effectiveBaseContent(variation, liveBaseContent),
    [liveBaseContent, variation]
  );
  const variationPlain = useMemo(
    () => variationContentToPlainText(variation.content),
    [variation.content]
  );

  const [originalVariationContent, setOriginalVariationContent] = useState(variationPlain);
  const [hunks, setHunks] = useState<ParagraphDiffHunk[]>(() =>
    diffChapterParagraphs(baseText, variationPlain)
  );
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [editingHunkId, setEditingHunkId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState("");
  const skipContentSyncRef = useRef(false);
  const prevVariationIdRef = useRef(variation.id);

  const originalHunks = useMemo(
    () => diffChapterParagraphs(baseText, originalVariationContent),
    [baseText, originalVariationContent]
  );

  useEffect(() => {
    const variationChanged = prevVariationIdRef.current !== variation.id;
    if (variationChanged) {
      prevVariationIdRef.current = variation.id;
      setOriginalVariationContent(variationPlain);
      setHunks(diffChapterParagraphs(baseText, variationPlain));
      setEditingHunkId(null);
      setEditDraft("");
      skipContentSyncRef.current = false;
      return;
    }

    if (skipContentSyncRef.current) {
      skipContentSyncRef.current = false;
      return;
    }

    const nextHunks = diffChapterParagraphs(baseText, variationPlain);
    setHunks((current) => preserveHunkPreviewState(nextHunks, current));
  }, [variation.id, baseText, variationPlain]);

  const changedCount = useMemo(() => countChangedHunks(hunks), [hunks]);
  const appliedCount = useMemo(() => countAppliedHunks(hunks), [hunks]);
  const unchangedCount = useMemo(
    () => hunks.filter((hunk) => !hunkHasMaterialChange(hunk)).length,
    [hunks]
  );

  const visibleHunks = useMemo(
    () => (showUnchanged ? hunks : hunks.filter((hunk) => hunkHasMaterialChange(hunk))),
    [hunks, showUnchanged]
  );

  const persistHunks = useCallback(
    (nextHunks: ParagraphDiffHunk[]) => {
      setHunks(nextHunks);
      if (!onVariationContentChange) return;
      skipContentSyncRef.current = true;
      onVariationContentChange(rebuildVariationContentFromHunks(nextHunks));
    },
    [onVariationContentChange]
  );

  const toggleHunk = useCallback((hunkId: string) => {
    setHunks((current) =>
      current.map((hunk) =>
        hunk.id === hunkId && hunkHasMaterialChange(hunk) ? { ...hunk, useVariation: !hunk.useVariation } : hunk
      )
    );
  }, []);

  const applyAllToPreview = useCallback(() => {
    setHunks((current) =>
      current.map((hunk) =>
        hunkHasMaterialChange(hunk) ? { ...hunk, useVariation: true } : hunk
      )
    );
  }, []);

  const revertAllPreview = useCallback(() => {
    setHunks((current) =>
      current.map((hunk) =>
        hunkHasMaterialChange(hunk)
          ? {
              ...hunk,
              useVariation: false,
            }
          : hunk
      )
    );
  }, []);

  const handleSaveToManuscript = useCallback(() => {
    onApply(mergeSelectedHunks(hunks), hunks);
  }, [hunks, onApply]);

  const startHunkEdit = useCallback((hunk: ParagraphDiffHunk) => {
    setEditingHunkId(hunk.id);
    setEditDraft(hunkVariationText(hunk));
  }, []);

  const cancelHunkEdit = useCallback(() => {
    setEditingHunkId(null);
    setEditDraft("");
  }, []);

  const saveHunkEdit = useCallback(() => {
    if (!editingHunkId) return;
    const nextHunks = hunks.map((hunk) =>
      hunk.id === editingHunkId ? applyHunkVariationText(hunk, editDraft) : hunk
    );
    persistHunks(nextHunks);
    setEditingHunkId(null);
    setEditDraft("");
  }, [editDraft, editingHunkId, hunks, persistHunks]);

  const revertHunkToOriginal = useCallback(
    (hunk: ParagraphDiffHunk) => {
      const original = findMatchingOriginalHunk(hunk, originalHunks);
      if (!original) return;
      const nextHunks = hunks.map((candidate) =>
        candidate.id === hunk.id
          ? {
              ...candidate,
              variationParagraphs: [...original.variationParagraphs],
              variationUnitParagraphIndices: [...original.variationUnitParagraphIndices],
            }
          : candidate
      );
      persistHunks(nextHunks);
      if (editingHunkId === hunk.id) {
        setEditingHunkId(null);
        setEditDraft("");
      }
    },
    [editingHunkId, hunks, originalHunks, persistHunks]
  );

  return (
    <Dialog
      open={true}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="flex h-[88vh] w-[min(1400px,96vw)] max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-none">
        <Card className="min-h-0 flex-1 gap-0 overflow-hidden rounded-none border-0 bg-card py-0 ring-0">
          <header className="flex items-start justify-between gap-4 border-b border-border bg-muted/30 px-4 py-3">
            <div className="min-w-0">
              <DialogTitle className="m-0 text-[0.95rem] font-semibold">Compare variation</DialogTitle>
              <DialogDescription className="mt-1 text-xs">
                {chapterTitle} · {changedCount} change{changedCount === 1 ? "" : "s"}
                {appliedCount > 0 ? ` · ${appliedCount} applied to preview` : ""}
                {variation.note ? ` · ${variation.note}` : ""}
              </DialogDescription>
              <p className="mt-1.5 max-w-2xl text-[0.72rem] leading-[1.45] text-muted-foreground">
                Red strikethrough is removed from the manuscript; green is new text. Each row is one replace/insert/delete
                block — formatting merges are grouped together. Apply updates the preview column in context.
                {canEdit
                  ? " Use Edit on a row to tweak that change; Restore original brings back the AI suggestion for that row."
                  : ""}
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 pr-8">
              {unchangedCount > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowUnchanged((value) => !value)}
                >
                  {showUnchanged ? "Hide unchanged" : `Show unchanged (${unchangedCount})`}
                </Button>
              ) : null}
              <Button type="button" variant="outline" size="sm" onClick={revertAllPreview}>
                Revert preview
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={applyAllToPreview} disabled={changedCount === 0}>
                Apply all to preview
              </Button>
              <Button type="button" size="sm" onClick={handleSaveToManuscript} disabled={appliedCount === 0}>
                Save preview to manuscript
              </Button>
            </div>
          </header>

          <div className={cn(comparisonGridClassName, "border-b border-border bg-muted/20")}>
            <div className="px-3 py-2 text-[0.7rem] font-semibold tracking-[0.04em] text-muted-foreground uppercase">
              Manuscript (kept / removed)
            </div>
            <div className="border-l border-border px-3 py-2 text-[0.7rem] font-semibold tracking-[0.04em] text-muted-foreground uppercase">
              Variation (new text)
            </div>
            <div className="border-l border-border px-3 py-2 text-[0.7rem] font-semibold tracking-[0.04em] text-muted-foreground uppercase">
              Reason
            </div>
            <div className="border-l border-border px-3 py-2 text-[0.7rem] font-semibold tracking-[0.04em] text-muted-foreground uppercase">
              Action
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            {visibleHunks.length === 0 ? (
              <Alert variant="info" className="m-4 w-auto text-center">
                <AlertDescription>No textual changes detected.</AlertDescription>
              </Alert>
            ) : null}
            {visibleHunks.map((hunk) => {
              const actionable = hunkHasMaterialChange(hunk);
              const applied = actionable && hunk.useVariation;
              const editable = canEdit && hunkIsEditable(hunk);
              const isEditing = editingHunkId === hunk.id;
              const originalHunk = findMatchingOriginalHunk(hunk, originalHunks);
              const customized = hunkVariationDiffersFromOriginal(hunk, originalHunk);

              return (
                <div
                  key={hunk.id}
                  data-kind={hunk.kind}
                  data-applied={applied || undefined}
                  className={cn(comparisonGridClassName, "border-b border-border/85")}
                >
                  <div
                    className={comparisonCellVariants({
                      state: !actionable
                        ? "equal"
                        : applied
                          ? "applied"
                          : hunk.kind !== "added"
                            ? "removed"
                            : "neutral",
                    })}
                  >
                    {actionable ? (
                      <Badge variant="ghost" className={hunkBadgeVariants({ kind: hunk.kind })}>
                        {hunkSummary(hunk)}
                      </Badge>
                    ) : null}
                    {renderHunkSide(hunk, "base", applied)}
                  </div>

                  <div
                    className={comparisonCellVariants({
                      state: !actionable
                        ? "equal"
                        : isEditing
                          ? "editing"
                          : hunk.kind === "removed"
                            ? "empty"
                            : "added",
                    })}
                  >
                    {actionable ? (
                      <Badge variant="ghost" className={hunkBadgeVariants({ kind: hunk.kind })}>
                        {hunkSummary(hunk)}
                      </Badge>
                    ) : null}
                    {isEditing ? (
                      <Textarea
                        aria-label="Edit variation text for this change"
                        className="min-h-28 font-[inherit] text-[0.92rem] leading-[1.65]"
                        value={editDraft}
                        onChange={(event) => setEditDraft(event.target.value)}
                        rows={6}
                        autoFocus
                      />
                    ) : (
                      renderHunkSide(hunk, "variation", applied)
                    )}
                  </div>

                  <div className={comparisonCellVariants()}>
                    {actionable ? (
                      canEdit ? (
                        <Textarea
                          aria-label="Reason for this change"
                          className="min-h-18 text-xs"
                          placeholder="Why this change? (optional)"
                          value={hunk.reason ?? ""}
                          onChange={(event) => {
                            const reason = event.target.value;
                            setHunks((current) =>
                              current.map((candidate) =>
                                candidate.id === hunk.id ? { ...candidate, reason } : candidate
                              )
                            );
                          }}
                          rows={3}
                        />
                      ) : (
                        <p className="text-xs whitespace-pre-wrap text-muted-foreground">
                          {hunk.reason?.trim() ? hunk.reason : "—"}
                        </p>
                      )
                    ) : null}
                  </div>

                  <div className="flex items-start justify-center border-l border-border/85 px-1.5 py-2.5">
                    {actionable ? (
                      <div className="flex w-full flex-col items-stretch gap-1.5 [&_button]:w-full [&_button]:justify-center [&_button]:px-1.5 [&_button]:text-[0.72rem]">
                        {isEditing ? (
                          <>
                            <Button type="button" size="sm" onClick={saveHunkEdit}>
                              Save
                            </Button>
                            <Button type="button" size="sm" variant="outline" onClick={cancelHunkEdit}>
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              type="button"
                              size="sm"
                              variant={applied ? "secondary" : "outline"}
                              onClick={() => toggleHunk(hunk.id)}
                            >
                              {applied ? "Undo apply" : "Apply →"}
                            </Button>
                            {editable ? (
                              <Button type="button" size="sm" variant="ghost" onClick={() => startHunkEdit(hunk)}>
                                Edit
                              </Button>
                            ) : null}
                            {editable && customized ? (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => revertHunkToOriginal(hunk)}
                              >
                                Restore original
                              </Button>
                            ) : null}
                          </>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </DialogContent>
    </Dialog>
  );
}
